import * as React from 'react';

import ActivitiesCell from './activitiesCell';
import DiagnosticJourney, { isAssignedPost } from './journeySteps';
import { EDIT_ACTIVITIES } from './packActionModals';
import PackActions from './packActions';
import ProgressHelp from './progressHelp';
import { ALL_CARD_PARTS, CardPart, STUDENTS } from './useShownCardParts';
import { ActivityChips, AssignedElsewhereNote, AssignedToCell, DetailRows, LearningToolChips, StudentCompletionBadges, completedCountFor, learningToolLabel } from './packCard';
import {
  DIAGNOSTIC_CLASSIFICATION_ID,
  Pack,
  RecommendationCounts,
  StudentCompletion,
  UnitProgress,
  diagnosticCardId,
  diagnosticResultsLink,
  diagnosticStudentResponsesLink,
  formatDate,
  isDefaultPackName,
  lastActiveFor,
  rosterCountFor,
} from './shared';

import { Tooltip, pluralize } from '../../../../Shared/index';

export interface DiagnosticRecord {
  activity_id: number;
  activity_name: string;
  assigned_count: number;
  assigned_date: string;
  classroom_id: number;
  completed_count: number;
  current_assigned_count?: number;
  current_completed_count?: number;
  post_test_id?: number;
  unit_id?: number;
  unit_ids?: number[];
  unit_name?: string;
  unit_template_id?: number;
  students?: Array<{ id: number, name: string | null, completed: boolean }>;
}

export interface UnassignedPost {
  activity_name: string;
  unit_template_id: number;
}

export interface DiagnosticCardData {
  classroomId: number;
  classroomName: string;
  name: string;
  pre: DiagnosticRecord;
  post?: DiagnosticRecord | UnassignedPost;
}

interface DiagnosticCardProps {
  card: DiagnosticCardData;
  recommendationCounts: RecommendationCounts;
  backingPack?: Pack;
  postBackingPack?: Pack;
  selectedClassroomId?: string | number;
  onPackChanged?: (snackbarCopy?: string) => void;
  isBoosted?: boolean;
  onToggleBoost?: () => void;
  postIsBoosted?: boolean;
  onTogglePostBoost?: () => void;
  isCompact?: boolean;
  activitySearch?: string;
  shownParts?: CardPart[];
  backingUnitProgress?: UnitProgress;
  postBackingUnitProgress?: UnitProgress;
  preStudents?: StudentCompletion[];
  postStudents?: StudentCompletion[];
}

function stripPhaseSuffix(activityName?: string): string | undefined {
  return activityName?.replace(/\s*\([^)]*\b(?:Baseline|Growth|Pre|Post)\b[^)]*\)\s*$/i, '').trim()
}

type DiagnosticTab = 'pre' | 'post'

const TABLIST_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End']

const ViewResultsButton = ({ disabled, href, testLabel }: { disabled: boolean, href?: string, testLabel: string }) => {
  if (disabled) {
    return (
      <Tooltip
        isTabbable={false}
        tooltipText={`No students have completed the ${testLabel} yet.`}
        tooltipTriggerText={
          <button aria-disabled="true" aria-label={`View results. No students have completed the ${testLabel} yet.`} className="quill-button extra-small contained disabled focus-on-light" type="button">View results</button>
        }
      />
    )
  }

  return (
    <a className="quill-button extra-small contained green focus-on-light" href={href} rel="noopener noreferrer" target="_blank">
      View results<span className="sr-only"> (opens in a new window)</span>
    </a>
  )
}

const DiagnosticDetails = ({ pack, record, unitProgress, customPackName, classroomId, activities, onManageActivities, shownParts, collapsesActivities, activitySearch }: { pack: Pack, record: DiagnosticRecord, unitProgress?: UnitProgress, customPackName: string | null, classroomId: number, activities: Array<{ id?: number, name: string }>, onManageActivities?: () => void, shownParts: CardPart[], collapsesActivities?: boolean, activitySearch?: string }) => {
  const rosterCount = rosterCountFor(pack, classroomId)
  const assignedDisplay = rosterCount ? Math.min(record.assigned_count, rosterCount) : record.assigned_count
  const lastActive = lastActiveFor(unitProgress, classroomId)
  const listedActivities = activities.map(activity => ({ activityId: activity.id ? String(activity.id) : '', classificationId: DIAGNOSTIC_CLASSIFICATION_ID, name: activity.name }))

  return (
    <DetailRows
      activitiesContent={(
        <ActivitiesCell onManageActivities={onManageActivities} pack={pack}>
          <ActivityChips activities={listedActivities} isCollapsible={collapsesActivities} searchQuery={activitySearch} />
        </ActivitiesCell>
      )}
      activitiesLabel={`${activities.length} ${pluralize(activities.length, 'activity', 'activities')}`}
      assignedOn={formatDate(record.assigned_date)}
      assignedTo={(
        <AssignedToCell
          countText={(
            <React.Fragment>
              {rosterCount ? `${assignedDisplay} of ${rosterCount}` : record.assigned_count} {pluralize(rosterCount || record.assigned_count, 'student', 'students')}
            </React.Fragment>
          )}
          note={<AssignedElsewhereNote classrooms={pack.classrooms} isDiagnostic={true} selectedClassroomId={classroomId} />}
          pack={pack}
        />
      )}
      customPackName={customPackName}
      lastActive={lastActive ? formatDate(lastActive) : '—'}
      packNameHint="The custom name you gave this pack. It's what students see on their dashboards. The card's title stays the diagnostic's name so it's easy to recognize here."
      shownParts={shownParts}
      toolChips={<LearningToolChips pack={pack} />}
      toolLabel={learningToolLabel(pack)}
    />
  )
}

const DiagnosticCard = ({ card, recommendationCounts, backingPack, postBackingPack, selectedClassroomId, onPackChanged, isBoosted, onToggleBoost, postIsBoosted, onTogglePostBoost, isCompact, activitySearch, shownParts = ALL_CARD_PARTS, backingUnitProgress, postBackingUnitProgress, preStudents, postStudents }: DiagnosticCardProps) => {
  const { classroomId, classroomName, name, pre, post } = card
  const isJourneyExpanded = !isCompact
  const [activeTab, setActiveTab] = React.useState<DiagnosticTab>('pre')
  const [showModal, setShowModal] = React.useState('')

  function activatePreTab() { setActiveTab('pre') }
  function activatePostTab() { setActiveTab('post') }
  function openManageActivities() { setShowModal(EDIT_ACTIVITIES) }

  const postAssigned = isAssignedPost(post)
  const postTestExists = Boolean(post || pre.post_test_id)

  const preTabRef = React.useRef<HTMLButtonElement>(null)
  const postTabRef = React.useRef<HTMLButtonElement>(null)

  function handleTablistKeyDown(event: React.KeyboardEvent) {
    if (!TABLIST_KEYS.includes(event.key)) { return }
    if (event.metaKey || event.ctrlKey || event.altKey) { return }

    event.preventDefault()
    if (event.key === 'ArrowRight' || event.key === 'End') {
      postTabRef.current?.focus()
      if (postAssigned && !isPostTab) { activatePostTab() }
    } else {
      preTabRef.current?.focus()
      if (isPostTab) { activatePreTab() }
    }
  }

  const tabsIdBase = `diagnostic-tabs-${pre.activity_id}-${classroomId}`
  const preTabId = `${tabsIdBase}-pre`
  const postTabId = `${tabsIdBase}-post`
  const panelId = `${tabsIdBase}-panel`

  const postActivityId = postAssigned ? (post as DiagnosticRecord).activity_id : pre.post_test_id
  const diagnosticActivities: Array<{ id?: number, name: string }> = [
    { id: pre.activity_id, name: pre.activity_name },
    ...(postTestExists && post?.activity_name ? [{ id: postActivityId, name: post.activity_name }] : []),
  ]

  const preTestLabel = postTestExists ? 'Baseline Pre' : 'diagnostic'

  const assignedPost = postAssigned ? post as DiagnosticRecord : null

  const showPostTab = postTestExists
  const isPostTab = showPostTab && postAssigned && activeTab === 'post'
  const activeTabId = isPostTab ? postTabId : preTabId

  const active = isPostTab
    ? {
      record: assignedPost || pre,
      testName: post?.activity_name,
      testLabel: 'Growth Post',
      backingPack: postBackingPack,
      unitProgress: postBackingUnitProgress,
      isBoosted: postIsBoosted,
      toggleBoost: onTogglePostBoost,
    }
    : {
      record: pre,
      testName: pre.activity_name,
      testLabel: preTestLabel,
      backingPack,
      unitProgress: backingUnitProgress,
      isBoosted,
      toggleBoost: onToggleBoost,
    }
  const activeTitle = stripPhaseSuffix(active.testName) || name
  const handleToggleBoost = active.toggleBoost

  const preClosed = Boolean(backingPack) && backingPack.open === false
  const postClosed = Boolean(postBackingPack) && postBackingPack.open === false
  const halfClosed = Boolean(backingPack && postBackingPack) && preClosed !== postClosed
  const defaultPackName = active.unitProgress?.template_name || active.record.activity_name
  const customPackName = active.backingPack && !isDefaultPackName(active.backingPack.unitName, defaultPackName)
    ? active.backingPack.unitName
    : null

  const resultsActivityId = isPostTab ? (pre.post_test_id || assignedPost?.activity_id) : pre.activity_id
  const resultsHref = resultsActivityId ? diagnosticResultsLink(resultsActivityId, classroomId, isPostTab) : undefined
  const resultsDisabled = active.record.completed_count === 0 || !resultsHref
  function reportHrefFor(student: StudentCompletion) {
    if (!resultsActivityId || !student.completed) { return undefined }
    return diagnosticStudentResponsesLink(resultsActivityId, classroomId, student.id)
  }

  const diagnosticStudents = (isPostTab ? postStudents : preStudents) || []
  const rosterCount = active.backingPack ? rosterCountFor(active.backingPack, classroomId) : 0
  const progressAssigned = diagnosticStudents.length || active.record.assigned_count
  const progressCompleted = diagnosticStudents.length ? completedCountFor(diagnosticStudents) : active.record.completed_count
  const progressHeading = progressAssigned
    ? `${Math.round((progressCompleted / progressAssigned) * 100)}% complete`
    : 'Progress'

  function renderPostTab() {
    if (!postAssigned) {
      return (
        <button
          aria-controls={panelId}
          aria-disabled="true"
          aria-label="Growth Post. The Growth Post hasn't been assigned yet."
          aria-selected={false}
          className="diagnostic-tab disabled focus-on-light"
          id={postTabId}
          onKeyDown={handleTablistKeyDown}
          ref={postTabRef}
          role="tab"
          tabIndex={-1}
          type="button"
        >
          <Tooltip
            isTabbable={false}
            tooltipText="The Growth Post hasn't been assigned yet."
            tooltipTriggerText="Growth Post"
          />
        </button>
      )
    }

    return (
      <button
        aria-controls={panelId}
        aria-selected={isPostTab}
        className={`diagnostic-tab focus-on-light ${isPostTab ? 'active' : ''}`}
        id={postTabId}
        onClick={activatePostTab}
        onKeyDown={handleTablistKeyDown}
        ref={postTabRef}
        role="tab"
        tabIndex={isPostTab ? 0 : -1}
        type="button"
      >
        Growth Post
        {halfClosed && postClosed && <span className="tab-closed-flag">· Closed</span>}
      </button>
    )
  }

  function renderTabStrip() {
    return (
      <div aria-label="Diagnostic test" className="diagnostic-tabs" role="tablist">
        <button
          aria-controls={panelId}
          aria-selected={!isPostTab}
          className={`diagnostic-tab focus-on-light ${!isPostTab ? 'active' : ''}`}
          id={preTabId}
          onClick={activatePreTab}
          onKeyDown={handleTablistKeyDown}
          ref={preTabRef}
          role="tab"
          tabIndex={isPostTab ? -1 : 0}
          type="button"
        >
          Baseline Pre
          {halfClosed && preClosed && <span className="tab-closed-flag">· Closed</span>}
        </button>
        {renderPostTab()}
      </div>
    )
  }

  return (
    <section className={`pack-card diagnostic-card ${isJourneyExpanded ? 'journey-expanded' : ''} ${active.isBoosted ? 'boosted' : ''}`} data-card-id={diagnosticCardId(card.classroomId, card.pre.activity_id)}>
      <div className="pack-card-header">
        <h3 title={activeTitle}>{activeTitle}</h3>
        {showPostTab && renderTabStrip()}
        {active.backingPack && onPackChanged && (
          <PackActions
            companionPack={isPostTab ? backingPack : postBackingPack}
            isBoosted={active.isBoosted}
            isDiagnostic={true}
            onPackChanged={onPackChanged}
            onToggleBoost={handleToggleBoost}
            pack={active.backingPack}
            selectedClassroomId={selectedClassroomId ?? classroomId}
            setShowModal={setShowModal}
            showModal={showModal}
          />
        )}
      </div>
      <div aria-labelledby={showPostTab ? activeTabId : undefined} className="diagnostic-tab-panel" id={showPostTab ? panelId : undefined} role={showPostTab ? 'tabpanel' : undefined}>
        <div className="pack-card-progress">
          <div className="progress-heading">
            <h4>{progressHeading}</h4>
            {diagnosticStudents.length > 0 && <ProgressHelp assigned={diagnosticStudents.length} classroomName={classroomName} completed={completedCountFor(diagnosticStudents)} isDiagnostic={true} pack={active.backingPack} roster={rosterCount} />}
          </div>
          {shownParts.includes(STUDENTS) && <StudentCompletionBadges reportHrefFor={reportHrefFor} showsNames={!isCompact} students={diagnosticStudents} />}
          <div className="pack-card-buttons">
            <ViewResultsButton disabled={resultsDisabled} href={resultsHref} testLabel={active.testLabel} />
          </div>
        </div>
        {isJourneyExpanded && (
          <DiagnosticJourney
            backingPackUnitId={backingPack?.unitId}
            classroomId={classroomId}
            post={post}
            postBackingPackUnitId={postBackingPack?.unitId}
            postTestExists={postTestExists}
            pre={pre}
            preTestLabel={preTestLabel}
            recommendationCounts={recommendationCounts}
          />
        )}
        <div className="pack-card-details">
          {active.backingPack && (
            <DiagnosticDetails activities={diagnosticActivities} activitySearch={activitySearch} classroomId={classroomId} collapsesActivities={isCompact} customPackName={customPackName} onManageActivities={onPackChanged ? openManageActivities : undefined} pack={active.backingPack} record={active.record} shownParts={shownParts} unitProgress={active.unitProgress} />
          )}
        </div>
      </div>
    </section>
  )
}

export default DiagnosticCard
